import { motion } from 'framer-motion';
import {
  Sparkles, Zap, Shield, Globe, Code, ArrowRight, ChevronDown,
} from 'lucide-react';
import useTranslation from '../hooks/useTranslation';
import useCompany from '../hooks/useCompany';

export default function Homepage() {
  const { t, locale, setLocale } = useTranslation();

  // Get company object
  const { company } = useCompany();

  // Extract company name
  const companyName = company.name;
  const companyFullName = company.fullName;
  const conmapnyAbriviated = company.abriviated || 'UTD';

  const serviceKeys = [
    'homepage.service_1',
    'homepage.service_2',
    'homepage.service_3',
    'homepage.service_4',
  ];

  const services = serviceKeys.map((key, index) => ({
    id: key,
    label: t(key),
    icon: [Zap, Shield, Globe, Code][index],
    gradient: [
      'from-purple-500 to-pink-500',
      'from-blue-500 to-cyan-500',
      'from-green-500 to-emerald-500',
      'from-orange-500 to-red-500',
    ][index],
  }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white min-h-screen py-3">
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center items-center text-center px-6 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="relative z-10 max-w-6xl mx-auto"
        >
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8"
          >
            <Sparkles className="w-4 h-4" />
            <span className="text-sm">{t('homepage.hero_badge')}</span>
          </motion.div>

          {/* FIXED: Pass company parameter */}
          <motion.h1
            variants={itemVariants}
            className={`transition-item-float px-4 py-2 mx-2 my-2 rounded-lg transition-colors duration-200 ${
              'text-blue-700 hover:text-white bg-transparent hover:bg-indigo1 hover:from-blue-600 hover:to-blue-500 font-semibold'
            }`}
          >
            {t('homepage.hero_title', { company: companyFullName })}
          </motion.h1>

          {/* FIXED: Pass company parameter */}
          <motion.p
            variants={itemVariants}
            className="text-xl md:text-2xl text-gray-300 mt-6 max-w-3xl mx-auto"
          >
            {t('homepage.hero_subtitle', { company: companyName })}
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="mt-12 inline-flex items-center gap-4 bg-white/5 backdrop-blur-sm p-4 rounded-2xl border border-white/10"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="font-bold text-lg">★</span>
              </div>
              <div className="text-left">
                {/* <p className="text-sm text-gray-400">{t('homepage.trusted_by')}</p> */}
                <p className="font-semibold">
                  {conmapnyAbriviated}
                  {' '}
                  {company.tagline}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
          >
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 group"
            >
              {t('homepage.cta_primary', { company: companyName })}
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </motion.a>
            <motion.a
              href="/team"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-white/10 backdrop-blur-sm rounded-xl font-semibold text-lg border border-white/20 hover:bg-white/20 transition-colors"
            >
              {t('homepage.cta_secondary')}
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="absolute bottom-8 left-0 right-0 mx-auto w-fit"
        >
          <ChevronDown className="w-6 h-6 text-gray-400" />

        </motion.div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              {t('homepage.service_title', { company: companyName })}
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('homepage.service_subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl blur-xl" />
                  <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-white/20 transition-all duration-300 h-full">
                    <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${service.gradient} mb-6`}>
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4">
                      {service.label}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {t(`${service.id}_desc`)}
                    </p>
                    <motion.div
                      className="mt-6 pt-6 border-t border-white/10"
                      initial={{ width: 0 }}
                      whileInView={{ width: '100%' }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 }}
                    >
                      <div className="h-0.5 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-20 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="text-center">
                <div
                  className="text-4xl md:text-6xl font-bold
                  bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  {t(`homepage.stat_${num}_value`)}
                </div>
                <div className="text-gray-400 mt-2">
                  {t(`homepage.stat_${num}_label`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section> */}

      {/* Why Choose Us Section */}
      <section id="about" className="py-20 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            {/* FIXED: Pass company parameter */}
            {t('homepage.why_choose', { company: companyName })}
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 max-w-3xl mx-auto text-lg"
          >
            {/* FIXED: Pass company parameter */}
            {t('homepage.why_choose_desc', { company: companyName })}
          </motion.p>
        </div>
      </section>

      {/* Call to Action */}
      <section id="contact" className="py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 mask-radial-farthest" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-xl rounded-3xl p-12 border border-white/10 shadow-2xl"
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              {t('homepage.cta_title')}
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              {t('homepage.cta_subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/contact-us"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl font-semibold text-lg inline-flex items-center justify-center gap-3 group shadow-lg shadow-blue-500/25"
              >
                {t('homepage.cta_btn')}
                <ArrowRight className="group-hover:translate-x-2 transition-transform" />
              </motion.a>

              <motion.a
                href="/team"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 bg-white/10 backdrop-blur-sm rounded-xl font-semibold text-lg border border-white/20 hover:bg-white/20 transition-colors"
              >
                {t('homepage.cta_secondary')}
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Language Switcher */}
      {/* Golden Ratio (apprx) 8:5 */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed bottom-8 right-5 z-50"
      >
        <div className="flex gap-2 bg-gray-800/80 backdrop-blur-sm rounded-full p-2 border border-white/10">
          {[
            { code: 'en', name: 'English', flag: '🇺🇸' },
            { code: 'bn', name: 'বাংলা', flag: '🇧🇩' },
          ].map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => setLocale(lang.code)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                locale === lang.code
                  ? 'bg-white text-gray-900'
                  : 'hover:bg-white/20'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
